import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../context/AppContext';
import Modal from '../../components/common/Modal';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import toast from 'react-hot-toast';
import { Plus, Edit2, Trash2, Search, ShieldCheck, Check } from 'lucide-react';

export default function Roles() {
  const { api } = useAuth();
  const [roles,     setRoles]     = useState([]);
  const [panels,    setPanels]    = useState([]);
  const [permTypes, setPermTypes] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [search,    setSearch]    = useState('');
  const [modal,     setModal]     = useState(false);
  const [editing,   setEditing]   = useState(null);
  const [saving,    setSaving]    = useState(false);
  const [deleting,  setDeleting]  = useState(null);
  const [delLoad,   setDelLoad]   = useState(false);

  // form state
  const [name,    setName]    = useState('');
  const [desc,    setDesc]    = useState('');
  const [panelId, setPanelId] = useState('');
  const [modules, setModules] = useState([]);
  // perms: { module_id: [permission_type_id, ...] }
  const [perms,   setPerms]   = useState({});

  const load = useCallback(async () => {
    const [r, p, pt] = await Promise.all([
      api.get('/admin/roles'),
      api.get('/admin/panels'),
      api.get('/admin/permission-types'),
    ]);
    setRoles(r.data); setPanels(p.data); setPermTypes(pt.data);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const loadModules = async pid => {
    if (!pid) { setModules([]); setPerms({}); return; }
    const r = await api.get(`/admin/panels/${pid}/modules`);
    setModules(r.data);
    const init = {};
    r.data.forEach(m => { init[m.id] = []; });
    setPerms(init);
  };

  const openCreate = () => {
    setEditing(null); setName(''); setDesc(''); setPanelId(''); setModules([]); setPerms({}); setModal(true);
  };

  const openEdit = async role => {
    setEditing(role);
    setName(role.name); setDesc(role.description||''); setPanelId(String(role.panel_id));
    const [mods, detail] = await Promise.all([
      api.get(`/admin/panels/${role.panel_id}/modules`),
      api.get(`/admin/roles/${role.id}`),
    ]);
    setModules(mods.data);
    const p = {};
    mods.data.forEach(m => { p[m.id] = []; });
    detail.data.permissions.forEach(pm => {
      if (p[pm.module_id] !== undefined) p[pm.module_id] = pm.permission_type_ids || [];
    });
    setPerms(p); setModal(true);
  };

  const handlePanelChange = pid => { setPanelId(pid); loadModules(pid); };

  // Toggle single permission type for a module
  const togglePerm = (mid, permTypeId) => {
    setPerms(p => {
      const current = p[mid] || [];
      return {
        ...p,
        [mid]: current.includes(permTypeId)
          ? current.filter(id => id !== permTypeId)
          : [...current, permTypeId],
      };
    });
  };

  // Toggle all permission types for a module
  const toggleAllFor = (mid, enable) => {
    setPerms(p => ({ ...p, [mid]: enable ? permTypes.map(pt => pt.id) : [] }));
  };

  const save = async e => {
    e.preventDefault();
    if (!panelId) return toast.error('Select a panel');
    const permissions = Object.entries(perms).map(([module_id, permission_type_ids]) => ({
      module_id: Number(module_id), permission_type_ids,
    }));
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/admin/roles/${editing.id}`, { name, description:desc, is_active:editing.is_active, permissions });
        toast.success('Role updated');
      } else {
        await api.post('/admin/roles', { name, description:desc, panel_id:Number(panelId), permissions });
        toast.success('Role created');
      }
      setModal(false); load();
    } catch (err) { toast.error(err.response?.data?.message || 'Error'); }
    finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    setDelLoad(true);
    try {
      await api.delete(`/admin/roles/${deleting.id}`);
      toast.success('Role deleted'); setDeleting(null); load();
    } catch { toast.error('Cannot delete – employees are assigned to this role'); }
    finally { setDelLoad(false); }
  };

  const filtered = roles.filter(r => r.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">Roles &amp; Permissions</h1>
          <p className="page-subtitle">Create roles for your panels and define granular permissions.</p>
        </div>
        <button className="btn btn-primary" onClick={openCreate}><Plus size={15}/> New Role</button>
      </div>

      <div className="search-bar mb-6" style={{ maxWidth:320 }}>
        <Search size={15} color="var(--text-muted)" />
        <input placeholder="Search roles…" value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      {loading ? (
        <div className="flex-center" style={{ minHeight:200 }}><span className="spinner" style={{ width:32,height:32 }}/></div>
      ) : (
        <div className="grid-3">
          {filtered.map(role => (
            <div className="card card-pad" key={role.id} style={{ position:'relative', overflow:'hidden' }}>
              <div style={{ position:'absolute', top:0, left:0, right:0, height:3, background: role.panel_color || 'var(--brand)' }} />
              <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:12, marginTop:6 }}>
                <div style={{ width:40, height:40, borderRadius:'var(--r-md)', background:'rgba(91,106,245,.12)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                  <ShieldCheck size={20} color="var(--brand)" />
                </div>
                <div style={{ display:'flex', gap:4 }}>
                  <button className="btn btn-ghost btn-sm btn-icon" onClick={() => openEdit(role)}><Edit2 size={14}/></button>
                  <button className="btn btn-ghost btn-sm btn-icon" onClick={() => setDeleting(role)}><Trash2 size={14}/></button>
                </div>
              </div>
              <div style={{ fontFamily:'var(--font-display)', fontWeight:700, marginBottom:4 }}>{role.name}</div>
              <div style={{ display:'flex', gap:6, marginBottom:8 }}>
                <span className="badge badge-panel" style={{ borderColor:role.panel_color, color:role.panel_color, background:role.panel_color+'18' }}>
                  {role.panel_name}
                </span>
                <span className={`badge ${role.is_active?'badge-active':'badge-inactive'}`}>
                  {role.is_active?'Active':'Inactive'}
                </span>
              </div>
              {role.description && <p style={{ fontSize:'.78rem', color:'var(--text-muted)', marginBottom:8 }}>{role.description}</p>}
              <div className="divider" />
              <div style={{ display:'flex', justifyContent:'space-between', fontSize:'.8rem', color:'var(--text-muted)' }}>
                <span>{role.sub_admin_count} employee{role.sub_admin_count!==1?'s':''}</span>
                <span>{new Date(role.created_at).toLocaleDateString()}</span>
              </div>
            </div>
          ))}
          {!filtered.length && (
            <div className="empty-state" style={{ gridColumn:'1/-1' }}>
              <ShieldCheck size={40}/>
              <h3>No roles found</h3>
              <p>Create your first role to define permissions for employees.</p>
            </div>
          )}
        </div>
      )}

      {/* Role Modal */}
      <Modal open={modal} onClose={() => setModal(false)} title={editing ? 'Edit Role' : 'Create Role'} size="modal-xl">
        <form onSubmit={save}>
          <div className="modal-body">
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Role Name <span className="req">*</span></label>
                <input className="form-input" placeholder="e.g. Content Manager" value={name}
                  onChange={e => setName(e.target.value)} required />
              </div>
              <div className="form-group">
                <label className="form-label">Panel <span className="req">*</span></label>
                <select className="form-select" value={panelId} onChange={e => handlePanelChange(e.target.value)} required disabled={!!editing}>
                  <option value="">— Select Panel —</option>
                  {panels.map(p => <option key={p.id} value={p.id}>{p.name} ({p.code})</option>)}
                </select>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Description</label>
              <textarea className="form-textarea" placeholder="What does this role do?" value={desc}
                onChange={e => setDesc(e.target.value)} />
            </div>

            {/* Dynamic Permissions Matrix */}
            {modules.length > 0 && permTypes.length > 0 && (
              <div>
                <div style={{ fontWeight:700, marginBottom:10, fontSize:'.875rem' }}>
                  Permissions Matrix
                  <span style={{ fontWeight:400, color:'var(--text-muted)', marginLeft:8, fontSize:'.78rem' }}>
                    ({modules.length} modules · {permTypes.length} permission types)
                  </span>
                </div>
                <div style={{ border:'1px solid var(--border)', borderRadius:'var(--r-md)', overflow:'hidden' }}>
                  <table className="perm-table">
                    <thead>
                      <tr>
                        <th style={{ width:'30%' }}>Module</th>
                        {permTypes.map(pt => <th key={pt.id}>{pt.label}</th>)}
                        <th>All</th>
                      </tr>
                    </thead>
                    <tbody>
                      {modules.map(m => {
                        const grantedIds = perms[m.id] || [];
                        const allOn = permTypes.length > 0 && permTypes.every(pt => grantedIds.includes(pt.id));
                        return (
                          <tr key={m.id}>
                            <td>
                              <div style={{ fontWeight:600 }}>{m.module_name}</div>
                              <div style={{ fontSize:'.7rem', color:'var(--text-muted)' }}>{m.module_key}</div>
                            </td>
                            {permTypes.map(pt => (
                              <td key={pt.id}>
                                <input type="checkbox" className="perm-checkbox"
                                  checked={grantedIds.includes(pt.id)}
                                  onChange={() => togglePerm(m.id, pt.id)} />
                              </td>
                            ))}
                            <td>
                              <button type="button"
                                onClick={() => toggleAllFor(m.id, !allOn)}
                                style={{
                                  width:22, height:22, borderRadius:'var(--r-sm)',
                                  border:`1.5px solid ${allOn ? 'var(--brand)' : 'var(--border)'}`,
                                  background: allOn ? 'var(--brand)' : 'transparent',
                                  cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center',
                                  transition:'all .15s', margin:'0 auto',
                                }}
                              >
                                {allOn && <Check size={12} color="white" strokeWidth={3}/>}
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            {panelId && modules.length === 0 && (
              <div style={{ padding:'20px', textAlign:'center', color:'var(--text-muted)', fontSize:'.875rem',
                background:'var(--bg-input)', borderRadius:'var(--r-md)', border:'1px solid var(--border)' }}>
                No modules configured for this panel. Ask Super Admin to add modules.
              </div>
            )}
            {permTypes.length === 0 && panelId && (
              <div style={{ padding:'20px', textAlign:'center', color:'var(--warning)', fontSize:'.875rem',
                background:'var(--bg-input)', borderRadius:'var(--r-md)', border:'1px solid var(--border)', marginTop:8 }}>
                No permission types defined. Ask Super Admin to configure permission types.
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={() => setModal(false)}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? <span className="spinner"/> : editing ? 'Update Role' : 'Create Role'}
            </button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={confirmDelete}
        loading={delLoad}
        title="Delete Role"
        message={`Delete role "${deleting?.name}"? It will be soft-deleted and hidden from active views.`}
      />
    </>
  );
}
